// frontend/src/components/preferences/DynamicPreferenceField.js - Themed version with enhanced hover effects

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPreferenceIcon } from '../../utils/preferenceIcons';
import referenceService from '../../services/referenceService';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import MultiLanguageField from '../common/MultiLanguageField';

const parseDefinitionRules = (rules) => {
  if (!rules) {
    return null;
  }

  if (typeof rules === 'string') {
    try {
      return JSON.parse(rules);
    } catch (error) {
      return null;
    }
  }

  return typeof rules === 'object' && !Array.isArray(rules) ? rules : null;
};

/**
 * Dynamically renders a preference field based on its definition
 * @param {Object} definition - The preference definition from backend
 * @param {*} value - The current value of the preference
 * @param {Function} onChange - Callback when value changes
 * @param {boolean} disabled - Whether the field is disabled
 * @param {number} entityId - The entity ID for multi-language support
 * @param {Object} preferenceMetadata - Metadata containing record ID for translations
 */
const DynamicPreferenceField = ({ definition, value, onChange, disabled = false, entityId, preferenceMetadata }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  const { entitySettings } = usePreferences();
  const [referenceOptions, setReferenceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [integerInputValue, setIntegerInputValue] = useState('');

  // Extract multi-language settings from entity settings
  const entityDefaultLanguage = entitySettings?.entity_default_language || entitySettings?.default_language || 'en';
  const multiLanguageEnabled = entitySettings?.multi_language_enabled === true;
  
  const Icon = getPreferenceIcon(definition.preference_key);
  const validationRules = parseDefinitionRules(definition.validation_rules);
  const referenceSource = definition.reference_source || validationRules?.reference_table || null;
  
  // Load reference data if needed
  useEffect(() => {
    if (definition.data_type === 'reference' && referenceSource) {
      loadReferenceData(referenceSource);
    } else {
      setReferenceOptions([]);
    }
  }, [definition.data_type, referenceSource]);
  
  const loadReferenceData = async (source) => {
    try {
      setLoading(true);
      
      // New settings-definition responses expose a public reference_source token
      // instead of raw reference table/column metadata. The fallback above keeps
      // this component compatible with older admin/internal definition shapes.
      if (source === 'countries') {
        const countries = await referenceService.getCountries();
        setReferenceOptions(countries);
      } else if (source === 'timezones') {
        const timezones = await referenceService.getTimezones();
        setReferenceOptions(timezones);
      } else if (source === 'languages') {
        const languages = await referenceService.getLanguages();
        setReferenceOptions(languages);
      } else {
        setReferenceOptions([]);
      }
    } catch (error) {
      console.error('Error loading reference data:', error);
      setReferenceOptions([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (definition.data_type !== 'integer') {
      return;
    }

    if (value === null || value === undefined || value === '') {
      setIntegerInputValue((prev) => {
        if (prev === '' || prev === '-') {
          return prev;
        }

        const fallback = definition.default_value;
        if (fallback === null || fallback === undefined || fallback === '') {
          return '';
        }

        const fallbackString = String(fallback);
        return fallbackString;
      });
      return;
    }

    const nextValue = String(value);
    setIntegerInputValue((prev) => (prev === nextValue ? prev : nextValue));
  }, [definition.data_type, definition.default_value, value]);

  const handleIntegerChange = (event) => {
    const rawValue = event.target.value;

    if (rawValue === '' || rawValue === '-') {
      // Preserve temporary blank or minus while the user is typing
      setIntegerInputValue(rawValue);
      onChange(definition.preference_key, null);
      return;
    }

    const normalized = rawValue.replace(/^(-?)0+(?=\d)/, '$1');
    setIntegerInputValue(normalized);

    const parsed = parseInt(normalized, 10);
    onChange(
      definition.preference_key,
      Number.isNaN(parsed) ? null : parsed
    );
  };
  
  // Get label for the preference - try translation with _label suffix first
  // (description is shown separately below the field)
  const getLabel = () => {
    // Try translation with _label suffix (e.g., "booking_window_days_label")
    const labelKey = `${definition.preference_key}_label`;
    const translated = t(labelKey, { defaultValue: '' });

    if (translated) return translated;

    // Fall back to formatted preference key
    return definition.preference_key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Get translated option label
  const getOptionLabel = (optionValue) => {
    // For enum values, try translation with pattern: preferenceKey.value
    const translationKey = `${definition.preference_key}.${optionValue}`;
    const translated = t(translationKey, { defaultValue: '' });
    
    if (translated) return translated;
    
    // Fall back to the value itself with formatting
    return optionValue
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Render based on data type
  const renderField = () => {
    switch (definition.data_type) {
      case 'boolean':
        return (
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={value === true || value === 'true' || value === 1}
                onChange={(e) => onChange(definition.preference_key, e.target.checked)}
                disabled={disabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <span className={cn("ml-2 text-sm", text.secondary)}>
                <Icon className="inline w-4 h-4 mr-1" />
                {getLabel()}
              </span>
            </label>
            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
        
      case 'integer':
        return (
          <div>
            <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
              <Icon className="inline w-4 h-4 mr-1" />
              {getLabel()}
            </label>
            <input
              type="number"
              value={integerInputValue}
              onChange={handleIntegerChange}
              disabled={disabled}
              min={validationRules?.min}
              max={validationRules?.max}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            />
            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
        
      case 'string': {
        // Check if this preference is translatable
        const isTranslatable = definition.is_translatable === true || definition.is_translatable === 1;
        const hasRecordId = preferenceMetadata?.id;
        const shouldShowMultiLanguage = isTranslatable && hasRecordId && multiLanguageEnabled;

        // If translatable and has record, use MultiLanguageField (like QuestionCard)
        if (shouldShowMultiLanguage) {
          return (
            <div>
              <MultiLanguageField
                entityId={entityId}
                entityDefaultLanguage={entityDefaultLanguage}
                multiLanguageEnabled={multiLanguageEnabled}
                contentType="entity_preference"
                contentId={preferenceMetadata.id}
                fieldName="preference_value"
                defaultValue={value || definition.default_value || ''}
                onChange={(fieldName, newValue, isDefault) => {
                  // Only update the main value when the default language is changed
                  if (isDefault) {
                    onChange(definition.preference_key, newValue);
                  }
                }}
                label={
                  <span>
                    <Icon className="inline w-4 h-4 mr-1" />
                    {getLabel()}
                  </span>
                }
                disabled={disabled}
              />
              {definition.description && (
                <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
              )}
            </div>
          );
        }

        // Regular string input (not translatable or no record yet)
        return (
          <div>
            <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
              <Icon className="inline w-4 h-4 mr-1" />
              {getLabel()}
            </label>

            <input
              type="text"
              value={value || definition.default_value || ''}
              onChange={(e) => onChange(definition.preference_key, e.target.value)}
              disabled={disabled}
              maxLength={validationRules?.maxLength}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            />

            {/* Show hint for translatable preferences that don't have a saved record yet */}
            {isTranslatable && !hasRecordId && multiLanguageEnabled && (
              <p className={cn("mt-1 text-xs", text.muted)}>
                💡 {t('saveSettingsToEnableTranslations', { defaultValue: 'Save settings to enable translations' })}
              </p>
            )}

            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
      }
        
      case 'enum': {
        const allowedValues = typeof definition.allowed_values === 'string' 
          ? JSON.parse(definition.allowed_values) 
          : definition.allowed_values;
          
        return (
          <div>
            <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
              <Icon className="inline w-4 h-4 mr-1" />
              {getLabel()}
            </label>
            <select
              value={value || definition.default_value || ''}
              onChange={(e) => onChange(definition.preference_key, e.target.value)}
              disabled={disabled}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              {allowedValues && allowedValues.map(val => (
                <option key={val} value={val}>
                  {getOptionLabel(val)}
                </option>
              ))}
            </select>
            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
      }
        
      case 'reference':
        return (
          <div>
            <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
              <Icon className="inline w-4 h-4 mr-1" />
              {getLabel()}
            </label>
            <select
              value={value || definition.default_value || ''}
              onChange={(e) => onChange(definition.preference_key, e.target.value)}
              disabled={disabled || loading}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                (disabled || loading) && "opacity-50 cursor-not-allowed"
              )}
            >
              {loading ? (
                <option>{t('loading')}</option>
              ) : (
                referenceOptions.map(option => {
                  // Determine the value and label based on the reference table
                  let optionValue, optionLabel;
                  
                  if (option.iso_code) { // Countries
                    optionValue = option.iso_code;
                    optionLabel = option.name;
                  } else if (option.iana_name) { // Timezones
                    optionValue = option.iana_name;
                    optionLabel = `${option.display_name} (${option.utc_offset})`;
                  } else if (option.language_code) { // Languages
                    optionValue = option.language_code;
                    optionLabel = option.language_name;
                  }
                  
                  return (
                    <option key={optionValue} value={optionValue}>
                      {optionLabel}
                    </option>
                  );
                })
              )}
            </select>
            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
        
      case 'json':
        // For now, render as a text field with JSON validation
        return (
          <div>
            <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
              <Icon className="inline w-4 h-4 mr-1" />
              {getLabel()}
            </label>
            <textarea
              value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || '{}'}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  onChange(definition.preference_key, parsed);
                } catch (error) {
                  // Keep the raw value if it's not valid JSON yet
                  onChange(definition.preference_key, e.target.value);
                }
              }}
              disabled={disabled}
              rows={4}
              className={cn(
                "w-full px-3 py-2 border rounded-md transition-all duration-200 font-mono text-sm",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-700",
                text.primary,
                "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            />
            {definition.description && (
              <p className={cn("mt-1 text-xs", text.muted)}>{t(`${definition.preference_key}_description`, { defaultValue: definition.description })}</p>
            )}
          </div>
        );
        
      default:
        console.warn(`Unknown data type: ${definition.data_type} for preference: ${definition.preference_key}`);
        return null;
    }
  };
  
  return renderField();
};

export default DynamicPreferenceField;
